import { useMemo } from 'react';
import { EChart as ReactECharts } from '../../charts/EChart';
import { useAppContext } from '../../../providers/AppProvider';
import { clientColour } from '../../../helpers/clientColour';
import { formatLength, toKm } from '../../../helpers/formatLength';

/**
 * Who the factory actually sells to.
 *
 * Horizontal, because customer names are words and a vertical axis turns them
 * into tilted unreadable stubs. Each bar keeps the colour that customer wears
 * everywhere else in the app — the same derivation, so a name recognised on the
 * order monitor is recognised here without reading it.
 */

const TopClientsChart = ({ clients = [], limit = 8, style }) => {
  const { getThemeColor } = useAppContext();

  const top = useMemo(
    () => clients.slice(0, limit).slice().reverse(),
    [clients, limit]
  );

  const option = useMemo(() => {
    const axis = { color: getThemeColor('gray-800'), fontFamily: 'Nunito Sans' };

    return {
      grid: { left: 6, right: 24, top: 8, bottom: 4, containLabel: true },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        padding: [8, 12],
        backgroundColor: getThemeColor('gray-soft'),
        borderColor: getThemeColor('gray-300'),
        borderWidth: 1,
        textStyle: { color: getThemeColor('dark'), fontFamily: 'Nunito Sans' },
        formatter: params => {
          const c = top[params?.[0]?.dataIndex];
          if (!c) return '';
          return `<strong>${c.client}</strong><br/>
            ${c.orders} order${c.orders === 1 ? '' : 's'}<br/>
            ordered <strong>${formatLength(c.ordered)}</strong><br/>
            made ${formatLength(c.produced)} · from stock ${formatLength(
            c.stock
          )}`;
        }
      },
      xAxis: {
        type: 'value',
        name: 'km',
        nameTextStyle: { ...axis, fontSize: 11 },
        splitLine: { lineStyle: { color: getThemeColor('gray-200') } },
        axisLabel: axis
      },
      yAxis: {
        type: 'category',
        data: top.map(c => c.client),
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { ...axis, fontWeight: 600 }
      },
      series: [
        {
          type: 'bar',
          barMaxWidth: 20,
          itemStyle: {
            borderRadius: [0, 4, 4, 0],
            color: p => getThemeColor(clientColour(top[p.dataIndex]?.client))
          },
          label: {
            show: true,
            position: 'right',
            formatter: p => formatLength(top[p.dataIndex]?.ordered),
            ...axis,
            fontSize: 11,
            fontWeight: 600
          },
          data: top.map(c => toKm(c.ordered))
        }
      ]
    };
  }, [top, getThemeColor]);

  if (!top.length) {
    return (
      <div className="text-center text-body-tertiary py-6 fs-9">
        No customers in this period.
      </div>
    );
  }

  return <ReactECharts option={option} style={style} />;
};

export default TopClientsChart;
